import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SubscribeForm from "@/components/feature/SubscribeForm";
import SectionHeading from "@/components/feature/SectionHeading";
import Testimonials from "@/pages/home/components/Testimonials";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { contentPrinciples, siteInfo, siteStats } from "@/mocks/gossip";

const SITE_URL = "https://pjdflq.cn";

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 瓜田吃瓜",
    description:
      "了解瓜田吃瓜的品牌定位、内容原则与团队理念。我们只梳理公开信息、用时间线记录后续，让吃瓜群众看得更清楚。",
    keywords: "瓜田吃瓜介绍,内容原则,围观现场,蹲后续,吃瓜群众",
    path: "/about",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于瓜田吃瓜",
        url: `${SITE_URL}/about`,
        inLanguage: "zh-CN",
        description: "瓜田吃瓜的品牌介绍、网站定位、内容原则与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "瓜田吃瓜",
        url: `${SITE_URL}/`,
        email: siteInfo.email,
        foundingDate: siteInfo.since,
        description: siteInfo.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
        ],
      },
    ],
  });

  return (
    <>
      <SiteHeader />
      <main className="w-full">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              关于瓜田吃瓜
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              我们是一群常年蹲在瓜田里的普通吃瓜群众，因为受够了碎片化的爆料，决定自己动手把信息拼完整。
            </p>
          </div>
        </div>

        <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="h-64 w-full overflow-hidden rounded-lg bg-background-100 md:h-[26rem]">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20cozy%20modern%20newsroom%20desk%20with%20notebooks%2C%20coffee%20and%20a%20laptop%20over%20a%20warm%20cream%20desk%2C%20green%20plants%20and%20amber%20light%2C%20minimalist%20illustration%20style%2C%20soft%20gradients%2C%20clean%20background%2C%20no%20people%2C%20no%20text%2C%20high%20detail&width=1200&height=900&seq=gt-about-desk&orientation=landscape"
                alt="瓜田吃瓜编辑工作台插画，象征内容整理与信息核对"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <span className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium tracking-wide text-primary-800">
                <i className="ri-leaf-line text-sm" aria-hidden="true"></i>
                品牌介绍
              </span>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                把一条瓜，从头蹲到尾
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                <p>{siteInfo.intro}</p>
                <p>
                  我们相信，围观一件公众事件并不需要站队，但需要秩序。当所有信息都按时间排列，情绪就不会轻易盖过事实，反转也不再让人措手不及。
                </p>
                <p>
                  网站自 {siteInfo.since} 年开始持续更新，内容覆盖娱乐八卦、影视综艺、社会热点、音乐潮流等方向。我们不追求最快，但尽量做到最清楚。
                </p>
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/plaza"
                  className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  去看看正在更新的内容
                </Link>
                <Link
                  to="/#follow"
                  className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full border border-primary-300 px-6 py-3 text-sm font-medium text-primary-800 transition-colors hover:bg-primary-100"
                >
                  订阅蹲后续
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-primary-800" aria-label="网站数据概览">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-10 md:grid-cols-4 md:px-8 md:py-12">
            {siteStats.map((stat) => (
              <div key={stat.id}>
                <p className="font-heading text-3xl font-bold text-secondary-300 md:text-4xl">
                  {stat.value}
                  <span className="ml-1 text-sm font-medium text-background-200">{stat.unit}</span>
                </p>
                <p className="mt-2 text-sm text-background-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-50" aria-labelledby="principle-title">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <h2 id="principle-title" className="sr-only">
              内容原则
            </h2>
            <SectionHeading
              kicker="内容原则"
              title="我们坚持的四件事"
              desc="这些原则决定了我们跟进什么、怎么写，以及什么情况下选择不写。"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {contentPrinciples.map((principle) => (
                <div
                  key={principle.id}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-800">
                    <i className={`${principle.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {principle.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {principle.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Testimonials />

        <section className="w-full bg-background-100" aria-labelledby="contact-title">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <span className="mb-2 inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium tracking-wide text-accent-800">
                  <i className="ri-mail-send-line text-sm" aria-hidden="true"></i>
                  联系我们
                </span>
                <h2
                  id="contact-title"
                  className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl"
                >
                  投稿、纠错与商务合作
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  如果你有可核实的公开线索，或者发现我们整理的时间线有出入，欢迎写信告诉我们。请在邮件里附上公开链接和大致时间，方便我们核对。
                </p>
                <ul className="mt-6 space-y-4 text-sm">
                  <li className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-700">
                      <i className="ri-mail-line text-base" aria-hidden="true"></i>
                    </span>
                    <span className="text-foreground-800">投稿与纠错：{siteInfo.email}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-700">
                      <i className="ri-time-line text-base" aria-hidden="true"></i>
                    </span>
                    <span className="text-foreground-800">我们通常在 1-2 个工作日内回复</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-700">
                      <i className="ri-shield-check-line text-base" aria-hidden="true"></i>
                    </span>
                    <span className="text-foreground-800">不接收来源不明的私信内容</span>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-950">
                    订阅蹲后续提醒
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    留下邮箱，新内容整理好之后我们会优先推送。我们只发后续进展，不会打扰。
                  </p>
                  <div className="mt-5">
                    <SubscribeForm />
                  </div>
                  <p className="mt-4 text-xs text-foreground-500">
                    提交即表示你同意我们通过该邮箱向你发送内容更新。随时可以来信取消订阅。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}